'use client'

/**
 * Motion primitives.
 *
 * - `Reveal` — fade + slide-up a single element when it enters the viewport.
 * - `RevealItem` — same animation as a child of `Stagger`; reads parent
 *   variants instead of triggering its own scroll trigger.
 * - `Stagger` — wraps direct children in `RevealItem` (unless the child is
 *   already a `motion.*` element) and sequences their entrances.
 * - `MotionWords` — splits a string title into words, fades each one in
 *   sequentially. Replaces the bespoke `TitleWords` from `ui/section-header`.
 *
 * Every primitive respects `prefers-reduced-motion: reduce` — when set, the
 * animation is skipped and the element renders in its final state immediately.
 */

import React from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { Join } from '../../lib/utils'
import {
  inViewOnce,
  motionDistance,
  motionDuration,
  motionEase,
  staggerStep,
} from './tokens'

// ── Reveal ───────────────────────────────────────────────────────────────────

export interface RevealProps {
  children: React.ReactNode
  /** Pixel distance to slide up from. Default `motionDistance.md` (20px). */
  distance?: number
  /** Duration in seconds. Default `motionDuration.base` (0.6s). */
  duration?: number
  /** Delay before the animation starts. Default 0. */
  delay?: number
  className?: string
  /** Render tag. Default `div`. */
  as?: 'div' | 'span' | 'section' | 'article' | 'header' | 'footer'
}

export function Reveal({
  children,
  distance = motionDistance.md,
  duration = motionDuration.base,
  delay = 0,
  className,
  as = 'div',
}: RevealProps) {
  const reduce = useReducedMotion()
  const MotionTag = motion[as] as typeof motion.div
  if (reduce) {
    return React.createElement(as, { className }, children)
  }
  return (
    <MotionTag
      initial={{ opacity: 0, y: distance }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={inViewOnce}
      transition={{ duration, delay, ease: motionEase.out }}
      className={className}
    >
      {children}
    </MotionTag>
  )
}

// ── Stagger + RevealItem ─────────────────────────────────────────────────────

const itemVariants = {
  initial: { opacity: 0, y: motionDistance.md },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: motionDuration.base, ease: motionEase.out },
  },
}

const containerVariants = (step: number) => ({
  initial: {},
  animate: { transition: { staggerChildren: step } },
})

export interface RevealItemProps {
  children: React.ReactNode
  className?: string
}

export function RevealItem({ children, className }: RevealItemProps) {
  const reduce = useReducedMotion()
  if (reduce) return <div className={className}>{children}</div>
  return (
    <motion.div variants={itemVariants} className={className}>
      {children}
    </motion.div>
  )
}

export interface StaggerProps {
  children: React.ReactNode
  /** Time between each child's entrance, in seconds. */
  step?: number
  className?: string
  /** If false, animation triggers every time the block scrolls into view. */
  once?: boolean
}

/**
 * Wraps each direct child in a `RevealItem` so it inherits the parent's
 * stagger orchestration. Children that are already `motion.*` elements (or
 * already `RevealItem`s) are passed through untouched.
 */
export function Stagger({
  children,
  step = staggerStep,
  className,
  once = true,
}: StaggerProps) {
  const reduce = useReducedMotion()
  const wrapped = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child
    // Heuristic: skip wrapping if the child already exposes framer's variants
    // prop (i.e. it's a motion.* element or another RevealItem).
    const props = (child.props ?? {}) as { variants?: unknown }
    if (props.variants) return child
    return <RevealItem>{child}</RevealItem>
  })

  if (reduce) return <div className={className}>{wrapped}</div>

  return (
    <motion.div
      variants={containerVariants(step)}
      initial="initial"
      whileInView="animate"
      viewport={{ ...inViewOnce, once }}
      className={className}
    >
      {wrapped}
    </motion.div>
  )
}

// ── MotionWords ──────────────────────────────────────────────────────────────

function splitWords(text: string): string[] {
  return text.split(/\s+/).filter(Boolean)
}

function flattenToTokens(
  node: React.ReactNode,
): Array<string | React.ReactElement> {
  if (typeof node === 'string') return splitWords(node)
  if (React.isValidElement(node)) {
    if (node.type === 'br') return [node]
    const props = (node.props ?? {}) as { children?: React.ReactNode }
    return props.children ? flattenToTokens(props.children) : []
  }
  if (Array.isArray(node)) return node.flatMap(flattenToTokens)
  return []
}

function tokensToSrText(tokens: Array<string | React.ReactElement>): string {
  return tokens
    .map((t) => (typeof t === 'string' ? t : ' '))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
}

const wordsContainer = {
  initial: {},
  animate: { transition: { staggerChildren: 0.12, delayChildren: 0 } },
}
const wordItem = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: motionDuration.base, ease: motionEase.out },
  },
}

export function MotionWords({ children }: { children: React.ReactNode }) {
  const reduce = useReducedMotion()
  const tokens = flattenToTokens(children)
  const srText = tokensToSrText(tokens)

  if (reduce) return <>{children}</>

  return (
    <>
      <span className="sr-only">{srText}</span>
      <motion.span
        aria-hidden="true"
        variants={wordsContainer}
        initial="initial"
        whileInView="animate"
        viewport={inViewOnce}
        style={{ display: 'inline' }}
      >
        {tokens.map((token, i) =>
          typeof token === 'string' ? (
            <motion.span
              key={`${token}-${i}`}
              variants={wordItem}
              className="mr-[0.28em] inline-block last:mr-0"
            >
              {token}
            </motion.span>
          ) : (
            <React.Fragment key={`br-${i}`}>{token}</React.Fragment>
          ),
        )}
      </motion.span>
    </>
  )
}

// ── Parallax ────────────────────────────────────────────────────────────────

export interface ParallaxProps {
  children: React.ReactNode
  /** Maximum vertical drift in px as the element traverses the viewport. */
  amount?: number
  className?: string
}

/**
 * Subtle parallax wrapper. Translates `children` by `amount` px (default 24)
 * as the user scrolls past. Falls back to a static element when reduced
 * motion is requested.
 */
export function Parallax({ children, amount = 24, className }: ParallaxProps) {
  const reduce = useReducedMotion()
  if (reduce) return <div className={className}>{children}</div>
  return (
    <motion.div
      className={Join('will-change-transform', className)}
      initial={{ y: amount }}
      whileInView={{ y: -amount }}
      viewport={{ amount: 0.4 }}
      transition={{ duration: 1.2, ease: motionEase.inOut }}
    >
      {children}
    </motion.div>
  )
}
